// @file Handle picking attachments when composing a post
import { ContentPickerSource, SEARCH_MODE_BY_CONTENT_SOURCE } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useScreenRecorderStore } from '@@/pinia/screen_recorder_store'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import type { AttachmentProps } from '@@/types'
import { useVideoSubtitles } from '@@/vuecomposables/useVideoSubtitles'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, shallowRef } from 'vue'

const useUndoableRemovePoll = (): {
  removePoll: typeof removePoll
} => {
  const surfaceDraftsStore = useSurfaceDraftsStore()

  const { activeDraft } = storeToRefs(surfaceDraftsStore)
  const { updateActiveDraft } = surfaceDraftsStore
  const { setSnackbar, removeSnackbar } = useGlobalSnackbarStore()

  const tempPoll = shallowRef<AttachmentProps>()
  const removePollSnackbarId = ref('')

  const undoRemovingPoll = (): void => {
    if (tempPoll.value == null) return
    updateActiveDraft({ wish_content: { attachment_props: tempPoll.value } })
    removeSnackbar(removePollSnackbarId.value)
    tempPoll.value = undefined
    removePollSnackbarId.value = ''
  }

  const removePoll = (): void => {
    if (activeDraft.value?.wish_content?.attachment_props?.type !== 'poll') return
    tempPoll.value = activeDraft.value.wish_content.attachment_props
    updateActiveDraft({ wish_content: undefined })
    removePollSnackbarId.value = setSnackbar({
      message: __('Poll removed'),
      actionText: __('Undo'),
      actionTextActions: [() => undoRemovingPoll()],
    })
  }

  onBeforeUnmount(() => {
    removeSnackbar(removePollSnackbarId.value)
    tempPoll.value = undefined
    removePollSnackbarId.value = ''
  })

  return {
    removePoll,
  }
}

export const useSurfaceAttachmentPicker = (): {
  pickContentSourceIfSupported: typeof pickContentSourceIfSupported
  editPoll: typeof editPoll
  uploadFile: typeof uploadFile
  removeAttachment: typeof removeAttachment
  cancelLoadAttachment: typeof cancelLoadAttachment
  updateAltTextInActiveDraft: typeof updateAltTextInActiveDraft
  uploadVideoSubtitles: typeof uploadVideoSubtitles
  downloadVideoSubtitles: typeof downloadVideoSubtitles
} => {
  const surfaceDraftsStore = useSurfaceDraftsStore()

  const { activeDraft } = storeToRefs(surfaceDraftsStore)
  const { removeDraftAttachment, uploadFileActiveDraft, stopUploadFileActiveDraft } = surfaceDraftsStore
  const { showContentSourceMenu, startLinkInputMode, openAiImagePreview, pickContentSource } = useContentPickerStore()
  const { showPostContentPicker } = useSurfaceContentPickerStore()
  const screenRecorderStore = useScreenRecorderStore()

  const focusEditorAfterDone = (): void => {
    // TODO: implement
  }

  const pickContentSourceIfSupported = (source?: ContentPickerSource | 'see_all'): void => {
    showPostContentPicker()

    // If no source is provided, open the content source menu to let the user choose
    if (source == null || source === 'see_all') {
      showContentSourceMenu()
      focusEditorAfterDone()
      return
    }

    if (isAppUsing('screenRecorderV2') && source === ContentPickerSource.ScreenshareRecorder) {
      screenRecorderStore.showScreenRecorder()
      return
    }

    if (
      source === ContentPickerSource.FileBrowser ||
      (source === ContentPickerSource.PhotoBooth && device.mobile) ||
      (source === ContentPickerSource.VideoRecorder && device.mobile)
    ) {
      // These sources are handled by the hidden file input in @padlet/universal-post-editor#AttachmentPickerButton.vue
      return
    }

    if (source === ContentPickerSource.LinkInput) {
      showContentSourceMenu()
      focusEditorAfterDone()
      startLinkInputMode()
      return
    }

    if (source === ContentPickerSource.AiImageSearch) {
      showContentSourceMenu()
      openAiImagePreview()
      return
    }

    const searchFilter = SEARCH_MODE_BY_CONTENT_SOURCE[source]
    if (searchFilter != null) {
      showContentSourceMenu({ searchFilter, search: true })
      focusEditorAfterDone()
      // TODO: focus the search input
      return
    }

    pickContentSource({ source })
  }

  const editPoll = (): void => {
    showPostContentPicker()
    pickContentSource({ source: ContentPickerSource.Poll })
  }

  const updateAltTextInActiveDraft = (text: string): void => {
    surfaceDraftsStore.updateActiveDraft({
      wish_content: {
        attachment_props: { ...(activeDraft.value?.wish_content?.attachment_props ?? {}), alternative_text: text },
      },
    })
  }

  const wishHashid = computed(() => activeDraft.value?.hashid)
  const { uploadVideoSubtitles, downloadVideoSubtitles } = useVideoSubtitles(wishHashid)

  const uploadFile = (e: Event): void => {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (file != null) uploadFileActiveDraft({ file })
  }

  const { removePoll } = useUndoableRemovePoll()

  const removeAttachment = (): void => {
    if (activeDraft.value?.cid == null) return
    if (activeDraft.value?.wish_content?.attachment_props?.type === 'poll') {
      removePoll()
    } else {
      removeDraftAttachment(activeDraft.value.cid)
    }
  }

  const cancelLoadAttachment = (): void => {
    if (activeDraft.value?.uploadingFile != null) {
      stopUploadFileActiveDraft()
    } else {
      removeAttachment()
    }
  }

  return {
    pickContentSourceIfSupported,
    editPoll,
    uploadFile,
    removeAttachment,
    cancelLoadAttachment,
    updateAltTextInActiveDraft,
    uploadVideoSubtitles,
    downloadVideoSubtitles,
  }
}
